import type { SagaIterator } from 'redux-saga';
import { call, fork, put, select, takeEvery } from 'redux-saga/effects';
import { ROUTES_ID } from '../../routes/constants';
import { navigate } from '../../routes/router';
import {
  buildOrderAccount,
  getPlaceOrderError,
  watchPaymentMethods,
} from '../../shared/payment/paymentSagas';
import { createOrder, deleteCart } from '../../shared/requestSagas';
import { setPlacing } from '../../store/checkoutSlice';
import {
  setOrder,
  setPaymentError,
  setPaymentMethod,
} from '../../store/paymentSlice';
import type { CartProduct } from '../../types/cart';
import type { OrderProduct, OrderRequest } from '../../types/payment';
import { onConfirmPaymentMethod, onPurchase } from '../../utils/gtm';
import { getCookie } from '../../utils/utils';
import { resetCart } from '../sagas';

export function* watchPayment(): SagaIterator<void> {
  yield fork(watchPaymentMethods);
  yield takeEvery(setPaymentMethod, makeOrder);
}

function* makeOrder(): SagaIterator<void> {
  const { localization, session } = yield select((state) => state.app);
  const { catalog, persistentCart, productsPreview } = yield select(
    (state) => state.cart,
  );
  const billing = yield select((state) => state.billing);
  const { paymentMethod, selectedMethod } = yield select(
    (state) => state.payment,
  );

  if (!paymentMethod || !persistentCart || !localization) {
    return;
  }

  yield put(setPlacing(true));
  yield put(setPaymentError(null));

  onConfirmPaymentMethod({
    catalog,
    productsPreview,
    localization,
    session,
    email: billing.form.email,
    billingInfo: billing.info,
  });

  const products: OrderProduct[] = persistentCart.products.map(
    (product: CartProduct) => ({
      ratePlanId: product.ratePlan,
      seats: product.quantity,
      migrationPathId: product.migrationPathId,
      sourceRatePlan: product.sourceRatePlan,
    }),
  );

  const referralUcc = getCookie('referral_ucc');

  const request: OrderRequest = {
    promoCode:
      productsPreview?.promoItems && productsPreview.promoStatus === 'Applied'
        ? productsPreview.promoItems.promoCode
        : '',
    paymentMethod,
    products,
    ...(referralUcc && { referralUcc }),
    account: buildOrderAccount(billing, localization, session),
  };

  const order = yield call(createOrder, request);

  if (order?.orderNumber) {
    yield put(setOrder(order));

    onPurchase({
      catalog,
      productsPreview,
      localization,
      session,
      email: billing.form.email,
      billingInfo: billing.info,
      transactionId: order.orderNumber,
      paymentType: selectedMethod,
    });

    yield call(deleteCart);
    yield call(resetCart);
    yield put(navigate(ROUTES_ID.CONFIRMATION));
  } else {
    yield put(setPaymentError(getPlaceOrderError(order)));
  }

  yield put(setPlacing(false));
}
